import React, { useState, useEffect, useCallback, useMemo } from "react";
import { TextFieldProps } from "@mui/material";
import FormField from "./ui/FormField";
import { useCurrencyFormatter } from "../../contexts/CurrencyContext";

interface AmountInputProps
  extends Omit<TextFieldProps, "value" | "onChange" | "type"> {
  value: string | number;
  onChange: (value: string) => void;
  maxDecimals?: number;
  allowNegative?: boolean;
  locale?: string; // optional, defaults to browser locale
}

const AmountInput: React.FC<AmountInputProps> = ({
  value,
  onChange,
  maxDecimals = 2,
  allowNegative = false,
  placeholder = "0",
  locale,
  ...textFieldProps
}) => {
  const [displayValue, setDisplayValue] = useState<string>("");
  const { currencyLocale } = useCurrencyFormatter();

  // 2️⃣ Get locale separators
  const { decimalSeparator, groupSeparator } = useMemo(() => {
    const parts = new Intl.NumberFormat(currencyLocale).formatToParts(12345.6);
    return {
      decimalSeparator: parts.find((p) => p.type === "decimal")?.value || ".",
      groupSeparator: parts.find((p) => p.type === "group")?.value || ",",
    };
  }, [currencyLocale]);

  // 3️⃣ Format number with locale
  const formatNumber = useCallback(
    (numStr: string): string => {
      if (!numStr || numStr === "") return "";

      const isNegative = numStr.startsWith("-");
      const cleanNum = numStr.replace("-", "");

      const [integerPart, decimalPart] = cleanNum.split(".");
      const formattedInteger = integerPart.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        groupSeparator
      );

      let result = formattedInteger;
      if (decimalPart !== undefined) {
        result += decimalSeparator + decimalPart;
      }

      return isNegative ? "-" + result : result;
    },
    [decimalSeparator, groupSeparator]
  );

  // 4️⃣ Clean number back to normalized format
  const cleanNumber = useCallback(
    (formattedStr: string): string => {
      let cleaned = formattedStr.replace(
        new RegExp(`\\${groupSeparator}`, "g"),
        ""
      );
      if (decimalSeparator !== ".") {
        cleaned = cleaned.replace(decimalSeparator, ".");
      }
      return cleaned;
    },
    [decimalSeparator, groupSeparator]
  );

  // 5️⃣ Sync display value with external value
  useEffect(() => {
    const stringValue = value.toString();
    if (stringValue === "0") {
      setDisplayValue("");
    } else {
      setDisplayValue(formatNumber(stringValue));
    }
  }, [value, formatNumber]);

  const validateAndCleanInput = (input: string): string => {
    let cleaned = cleanNumber(input);

    // Remove invalid characters except digits, dot, minus
    cleaned = cleaned.replace(/[^\d.-]/g, "");

    // Handle negative numbers
    if (!allowNegative) {
      cleaned = cleaned.replace(/-/g, "");
    } else {
      const minusMatches = cleaned.match(/-/g);
      if (minusMatches && minusMatches.length > 1) {
        cleaned = cleaned.replace(/-/g, "");
        if (input.startsWith("-")) cleaned = "-" + cleaned;
      } else if (cleaned.includes("-") && !cleaned.startsWith("-")) {
        cleaned = cleaned.replace("-", "");
      }
    }

    // Only allow one decimal point
    const decimalMatches = cleaned.match(/\./g);
    if (decimalMatches && decimalMatches.length > 1) {
      const parts = cleaned.split(".");
      cleaned = parts[0] + "." + parts.slice(1).join("");
    }

    // Limit decimals
    if (cleaned.includes(".")) {
      const [integer, decimal] = cleaned.split(".");
      if (decimal && decimal.length > maxDecimals) {
        cleaned = integer + "." + decimal.substring(0, maxDecimals);
      }
    }

    // Remove leading zeros
    if (
      cleaned.length > 1 &&
      cleaned.startsWith("0") &&
      !cleaned.startsWith("0.")
    ) {
      cleaned = cleaned.replace(/^0+/, "") || "0";
    }

    // Handle negative zero
    if (
      cleaned.startsWith("-0") &&
      cleaned.length > 2 &&
      !cleaned.startsWith("-0.")
    ) {
      cleaned = "-" + cleaned.substring(2);
    }

    return cleaned;
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;

    // Cursor position before formatting
    const cursorPosition = event.target.selectionStart || 0;
    const prevGroupCount = (
      displayValue
        .substring(0, cursorPosition)
        .match(new RegExp(`\\${groupSeparator}`, "g")) || []
    ).length;

    // Clean and validate
    const cleanedValue = validateAndCleanInput(input);

    // Format for display
    const formattedValue = formatNumber(cleanedValue);

    // Cursor adjustment
    const newGroupCount = (
      formattedValue
        .substring(0, cursorPosition)
        .match(new RegExp(`\\${groupSeparator}`, "g")) || []
    ).length;
    const cursorOffset = newGroupCount - prevGroupCount;

    setDisplayValue(formattedValue);
    onChange(cleanedValue);

    setTimeout(() => {
      const inputEl = event.target;
      const newPos = Math.min(
        cursorPosition + cursorOffset,
        formattedValue.length
      );
      inputEl.setSelectionRange(newPos, newPos);
    }, 0);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      "Backspace",
      "Delete",
      "Tab",
      "Escape",
      "Enter",
      decimalSeparator,
      ".",
      ",",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
    ];
    const isNumber = event.key >= "0" && event.key <= "9";
    const isMinus = allowNegative && event.key === "-";
    const isCtrlCmd = event.ctrlKey || event.metaKey;

    if (
      !isNumber &&
      !allowedKeys.includes(event.key) &&
      !isMinus &&
      !isCtrlCmd
    ) {
      event.preventDefault();
    }

    // Prevent multiple decimals
    if (
      (event.key === decimalSeparator ||
        event.key === "." ||
        event.key === ",") &&
      cleanNumber(displayValue).includes(".")
    ) {
      event.preventDefault();
    }

    // Prevent minus if not at beginning
    if (event.key === "-" && event.currentTarget.selectionStart !== 0) {
      event.preventDefault();
    }

    textFieldProps.onKeyDown?.(event);
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) =>
    textFieldProps.onFocus?.(event);
  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const cleanValue = cleanNumber(displayValue);
    const numericValue = parseFloat(cleanValue) || 0;
    const finalValue = numericValue.toString();
    if (finalValue !== value.toString()) onChange(finalValue);
    textFieldProps.onBlur?.(event);
  };

  return (
    <FormField
      {...textFieldProps}
      value={displayValue}
      onChange={handleInputChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      inputProps={{
        ...textFieldProps.inputProps,
        inputMode: "decimal",
      }}
    />
  );
};

export default AmountInput;
